import React, { useRef } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { VariantReader } from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"

/**
 * Horizontal scroller with mouse drag and prev/next buttons that snap to
 * the first partially visible child (port of the old ScrollableBox).
 */
export default function ScrollableRow({
  children,
  variant = "default",
  styleOverrides,
  showNavigationButtons = true,
  className = "",
}) {
  const v = VariantReader.useVariant({ componentName: "ScrollableRow", variant, styleOverrides })
  const ref = useRef(null)
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false })

  const firstVisible = (dir) => {
    const el = ref.current
    if (!el) return null
    const items = [...el.children]
    const box = el.getBoundingClientRect()
    let min = Number.MAX_VALUE
    let minIdx = -1
    items.forEach((child, i) => {
      const rect = child.getBoundingClientRect()
      if (rect.right < box.left) return
      const dist = Math.abs(rect.right - box.left)
      if (dist < min) {
        min = dist
        minIdx = i
      }
    })
    const idx = Math.max(0, Math.min(items.length - 1, dir === "next" ? minIdx + 1 : minIdx - 1))
    return items[idx]
  }

  const scrollTo = (dir) => () => {
    const el = firstVisible(dir)
    if (el && ref.current) ref.current.scrollTo({ left: el.offsetLeft, behavior: "smooth" })
  }

  const onMouseDown = (e) => {
    drag.current = { active: true, startX: e.pageX, scrollLeft: ref.current.scrollLeft, moved: false }
  }
  const onMouseMove = (e) => {
    if (!drag.current.active) return
    const dx = e.pageX - drag.current.startX
    if (Math.abs(dx) > 4) drag.current.moved = true
    ref.current.scrollLeft = drag.current.scrollLeft - dx
  }
  const endDrag = () => {
    drag.current.active = false
  }
  // Swallow the click that ends a drag so cards are not opened accidentally
  const onClickCapture = (e) => {
    if (!drag.current.moved) return
    drag.current.moved = false
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <div className={twMerge(v.container, className)}>
      <div
        ref={ref}
        className={v.scroller}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onClickCapture={onClickCapture}
      >
        {children}
      </div>
      {showNavigationButtons && (
        <>
          <button type="button" aria-label="Previous" className={twMerge(v.button, v.buttonPrev)} onClick={scrollTo("prev")}>
            <ChevronLeftIcon className="w-6 h-6" />
          </button>
          <button type="button" aria-label="Next" className={twMerge(v.button, v.buttonNext)} onClick={scrollTo("next")}>
            <ChevronRightIcon className="w-6 h-6" />
          </button>
        </>
      )}
    </div>
  )
}
